import { Icon } from '@mdvip/shared/components/blocks/Icon';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalDescription,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Button } from '@mdvip/shared/components/ui/button';
import { Input } from '@mdvip/shared/components/ui/input';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { cn } from '@mdvip/shared/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMyPracticeMembers } from '@/hooks/members/useMyPracticeMembers';
import { filterMembers } from '@/lib/members/memberSearch';
import { useActiveMemberStore } from '@/stores/activeMemberStore';
import { MEMBER_CONTEXT_BAR_NAME_ID } from './MemberContextBar';
import { MemberOptionRow } from './MemberOptionRow';

/** 6 rows of the design's own `min-h-14` (3.5rem) is 21rem (`max-h-84`) — the rest scrolls, per AC2. */
const SKELETON_ROWS = 6;
const LIST_CLASSES = 'flex max-h-84 flex-col overflow-y-auto pr-2';

/**
 * The provider's "select a patient to begin" gate. Mounted unconditionally by `AwpProgramPage`;
 * its own open state derives from whether anyone is active yet (`activeMemberId === null`), not a
 * prop, so there is nothing for a caller to get out of sync.
 *
 * Can't be dismissed by the close button, the scrim, or Escape — only picking a row closes it —
 * except when the roster itself is empty, where there is nothing to pick and a non-dismissible
 * modal would trap the provider.
 */
export function SelectMemberModal({ className }: { className?: string } = {}) {
  const { t } = useTranslation('providers');
  const tError = useErrorTranslation();
  const { data: members, isPending, isError, error, refetch } = useMyPracticeMembers();
  const activeMemberId = useActiveMemberStore((state) => state.activeMemberId);
  const setActiveMember = useActiveMemberStore((state) => state.setActiveMember);

  const [query, setQuery] = useState('');
  const [dismissedEmpty, setDismissedEmpty] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const isEmptyRoster = !isPending && !isError && (members?.length ?? 0) === 0;
  const results = useMemo(() => filterMembers(members ?? [], query), [members, query]);
  // Stays usable while the roster loads: what's typed is applied the moment the list arrives, and
  // focus can land on open. Only an error or an empty roster leaves nothing to search.
  const searchDisabled = isError || isEmptyRoster;

  const open = activeMemberId === null && !(isEmptyRoster && dismissedEmpty);

  useEffect(() => {
    if (open && !searchDisabled) searchRef.current?.focus();
  }, [open, searchDisabled]);

  useEffect(() => {
    if (isError) {
      trackException(error instanceof Error ? error : new Error('myPracticeMembers failed'), {
        source: 'myPracticeMembers',
      });
    }
  }, [isError, error]);

  const choose = (contactId: string, name: string) => {
    setQuery('');
    setActiveMember(contactId);
    // The modal unmounts on the next render (activeMemberId is no longer null); the context bar's
    // name heading is already in the DOM underneath, so focus can move there immediately.
    requestAnimationFrame(() => {
      const heading = document.getElementById(MEMBER_CONTEXT_BAR_NAME_ID);
      heading?.focus();
      // The member's own name, not a templated phrase: the ticket's i18n list has no key for this
      // announcement, and the name is a value, not copy, so there's nothing to localize here.
      if (liveRegionRef.current) liveRegionRef.current.textContent = name;
    });
  };

  return (
    <ResponsiveModal
      open={open}
      className={cn('sm:max-w-lg', className)}
      showCloseButton={isEmptyRoster}
      onOpenChange={(next) => {
        // Base UI only ever asks to close (scrim, Escape, the close button). Honor it solely in
        // the zero-roster case — there is nothing to pick, so a non-dismissible modal would trap
        // the provider on a dead page.
        if (!next && isEmptyRoster) setDismissedEmpty(true);
      }}
    >
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('members.selectModal.title')}</ResponsiveModalTitle>
        <ResponsiveModalDescription>{t('members.selectModal.subtitle')}</ResponsiveModalDescription>
      </ResponsiveModalHeader>
      <ResponsiveModalBody className="flex flex-col gap-4">
        <div className="relative">
          <Icon
            name="search"
            size="xs"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('members.selectModal.searchLabel')}
            aria-label={t('members.selectModal.searchLabel')}
            className="pl-9"
            disabled={searchDisabled}
          />
        </div>

        {isError ? (
          <Alert variant="destructive">
            <Icon name="alertTriangle" weight="solid" />
            <AlertTitle>{tError('title')}</AlertTitle>
            <AlertDescription>
              <p>{tError(extractGqlErrorCode(error))}</p>
              <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => refetch()}>
                <Icon name="refreshCw" size="xs" />
                {t('members.contextBar.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : isPending ? (
          <ul className={LIST_CLASSES} aria-busy="true">
            <li className="sr-only" aria-live="polite">
              {t('members.selectModal.loadingLabel')}
            </li>
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton placeholder, not real data.
              <li key={i} aria-hidden="true" className="flex min-h-14 w-full items-center gap-4 px-2 py-2">
                <Skeleton className="size-9 shrink-0 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-12 shrink-0" />
              </li>
            ))}
          </ul>
        ) : isEmptyRoster ? (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">{t('members.selectModal.noPatients')}</p>
        ) : (
          <ul className={LIST_CLASSES}>
            {results.map((member) => {
              const name = [member.firstName, member.lastName].filter(Boolean).join(' ');
              return (
                <li key={member.id}>
                  <button
                    type="button"
                    onClick={() => choose(member.id, name)}
                    aria-label={name}
                    className="flex min-h-14 w-full items-center rounded-md px-2 py-2 text-left hover:bg-accent"
                  >
                    <MemberOptionRow member={member} />
                  </button>
                </li>
              );
            })}
            {results.length === 0 && (
              <li className="px-2 py-8 text-center text-sm text-muted-foreground">
                {t('members.selectModal.noMatch', { query: query.trim() })}
              </li>
            )}
          </ul>
        )}
      </ResponsiveModalBody>
      <div ref={liveRegionRef} className="sr-only" aria-live="polite" role="status" />
    </ResponsiveModal>
  );
}
